import {
    AnnounceIcon,
    BarGraphIcon,
    ClipboardIcon,
    CrmIcon,
    DashboardIcon,
    GlobeIcon,
    HandshakeIcon,
    PiggyBankIcon,
    ProductsIcon,
    RoundedSquareIcon,
    SettingsIcon,
} from "@xola/icons";
import { Sidebar } from "@xola/ui-kit";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { useNotifications } from "../../hooks/useNotifications";
import { useVisibility } from "../../hooks/useVisibility";
import { getPermissions, useCanViewXsite } from "../../stores/globalStore";
import { AdminMenu } from "./AdminMenu";
import { AppLogo } from "./AppLogo";
import { CustomersMenuItems } from "./CustomersMenuItems";
import { CustomMenuItems } from "./CustomMenuItems";
import { DistributionMenuItems } from "./DistributionMenuItems";
import { MarketingMenuItems } from "./MarketingMenuItems";
import { MenuItem } from "./MenuItem";
import { MenuSidebarFooter } from "./MenuSidebarFooter";
import { ProductMenuItems } from "./ProductMenuItems";
import { ReportMenuItems } from "./ReportMenuItems";
import { SettingMenuItems } from "./SettingMenuItems";
import { EmbeddedAppTriggerLocation } from "@/embeddedApps/EmbeddedAppTriggerLocation";
import { useMenuSidebarStore } from "@/stores/menuSidebarStore";

export const MenuSidebar = React.memo(() => {
    const history = useHistory();
    const handleLogoClick = () => history.push("/");

    const shouldCloseDrawer = useMenuSidebarStore((state) => state.shouldCloseDrawer);

    const [isLeftDrawerOpen, { toggle: toggleLeftDrawer, hide: closeLeftDrawer }] = useVisibility();
    const [isRightDrawerOpen, { toggle: toggleRightDrawer, hide: closeRightDrawer }] = useVisibility();

    const notifications = useNotifications();
    const permissions = getPermissions();
    const canViewXsite = useCanViewXsite();

    useEffect(() => {
        if (shouldCloseDrawer) {
            closeLeftDrawer();
            closeRightDrawer();
        }

        useMenuSidebarStore.setState({ shouldCloseDrawer: false });
        // we want to trigger this only on route change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldCloseDrawer]);

    const handleLeftDrawerClose = () => {
        closeLeftDrawer();
        notifications.announcements.onClose();
    };

    const handleRightDrawerClose = () => {
        closeRightDrawer();
        notifications.notices.onClose();
    };

    const handleDrawerStateChange = (drawer?: "left" | "right") => {
        if (drawer === "left") {
            if (isRightDrawerOpen) {
                handleRightDrawerClose();
            } else if (isLeftDrawerOpen) {
                notifications.announcements.onClose();
            }

            toggleLeftDrawer();
        } else if (drawer === "right") {
            if (isLeftDrawerOpen) {
                handleLeftDrawerClose();
            } else if (isRightDrawerOpen) {
                notifications.notices.onClose();
            }

            toggleRightDrawer();
        }
    };

    const showAdminMenu = window.location.origin === "http://localhost:3000" || permissions.canViewAdminMenu;

    return (
        <div className="h-screen">
            <Sidebar
                isFixed
                logo={<AppLogo onClick={handleLogoClick} />}
                footer={<MenuSidebarFooter />}
                className="print:hidden"
                notifications={notifications}
                isLeftDrawerOpen={isLeftDrawerOpen}
                isRightDrawerOpen={isRightDrawerOpen}
                handleDrawerStateChange={handleDrawerStateChange}
                onLogoClick={handleLogoClick}
            >
                {permissions.canViewDashboard && <MenuItem route="dashboard" icon={DashboardIcon} label="Dashboard" />}
                {permissions.canViewPurchases && <MenuItem route="purchases" icon={PiggyBankIcon} label="Purchases" />}
                {permissions.canViewCustomers && (
                    <MenuItem
                        icon={CrmIcon}
                        label="Customers"
                        route="customers"
                        content={
                            <>
                                <CustomersMenuItems />
                                <EmbeddedAppTriggerLocation id="customers.leftMenu" />
                            </>
                        }
                    />
                )}
                {permissions.canViewProducts && (
                    <MenuItem route="products" icon={ProductsIcon} label="Products" content={<ProductMenuItems />} />
                )}
                {(permissions.canViewGuides || permissions.canViewEquipments) && (
                    <MenuItem
                        icon={ClipboardIcon}
                        route="resources"
                        label="Resources"
                        content={
                            <>
                                {permissions.canViewEquipments && (
                                    <MenuItem isSubMenuItem route="resources/equipment" label="Equipment Management" />
                                )}
                                {permissions.canViewGuides && (
                                    <MenuItem isSubMenuItem route="resources/guides" label="Guide Management" />
                                )}
                                <CustomMenuItems route="resources" />
                                <EmbeddedAppTriggerLocation id="resources.leftMenu" />
                            </>
                        }
                    />
                )}
                {permissions.canViewReports && permissions.shouldShowReports && (
                    <MenuItem
                        label="Reports"
                        route="reports"
                        icon={BarGraphIcon}
                        content={
                            <>
                                <ReportMenuItems />
                                <EmbeddedAppTriggerLocation id="reports.leftMenu" />
                            </>
                        }
                    />
                )}
                {permissions.canViewMarketing && (
                    <MenuItem
                        icon={AnnounceIcon}
                        route="marketing"
                        label="Marketing"
                        content={
                            <>
                                <MarketingMenuItems />
                                <EmbeddedAppTriggerLocation id="marketing.leftMenu" />
                            </>
                        }
                    />
                )}
                {permissions.canViewDistribution && (
                    <MenuItem
                        icon={HandshakeIcon}
                        route="distribution"
                        label="Distribution"
                        content={
                            <>
                                <DistributionMenuItems />
                                <EmbeddedAppTriggerLocation id="distribution.leftMenu" />
                            </>
                        }
                    />
                )}
                {permissions.canViewAppStore && <MenuItem route="apps" icon={RoundedSquareIcon} label="App Store" />}
                {permissions.canViewSettings && (
                    <MenuItem
                        icon={SettingsIcon}
                        route="settings"
                        label="Settings"
                        content={
                            <>
                                <SettingMenuItems />
                                <EmbeddedAppTriggerLocation id="settings.leftMenu" />
                            </>
                        }
                    />
                )}
                {canViewXsite && (
                    <>
                        {/* A Website Manager delegate has no other menu item, so the separator would
                            otherwise lead the menu. */}
                        <Sidebar.Separator className="first:hidden" />
                        <MenuItem icon={GlobeIcon} label="XSite" route="xsite" />
                    </>
                )}
                {showAdminMenu && (
                    <ErrorBoundary fallbackMessage="Error">
                        <AdminMenu />
                    </ErrorBoundary>
                )}
            </Sidebar>
        </div>
    );
});

MenuSidebar.displayName = "MenuSidebar";
